import {
  useCallback,
  useEffect,
} from "react";

import {
  DASHBOARD_SCROLL_POSITION_KEY,
  DASHBOARD_SCROLL_SECTION_KEY,
  DASHBOARD_SCROLL_SECTION_OFFSET_KEY,
} from "../dashboard.constants";

type UseDashboardScrollOptions = {
  loading: boolean;
  dependency?: unknown;
  selectedCategory: string;
  setSelectedCategory: (
    value: string,
  ) => void;
};

const getCurrentScrollTop = () =>
  window.pageYOffset ||
  document.documentElement.scrollTop ||
  0;

const findCurrentDashboardSection = (
  currentScrollTop: number,
): HTMLElement | null => {
  const sectionElements = Array.from(
    document.querySelectorAll<HTMLElement>(
      "[data-dashboard-section]",
    ),
  );

  return (
    sectionElements
      .filter((element) => {
        const sectionTop =
          element.getBoundingClientRect().top +
          window.scrollY;

        return (
          sectionTop <= currentScrollTop + 120
        );
      })
      .sort((first, second) => {
        const firstTop =
          first.getBoundingClientRect().top +
          window.scrollY;

        const secondTop =
          second.getBoundingClientRect().top +
          window.scrollY;

        return secondTop - firstTop;
      })[0] ??
    sectionElements[0] ??
    null
  );
};

const useDashboardScroll = ({
  loading,
  dependency,
  selectedCategory,
  setSelectedCategory,
}: UseDashboardScrollOptions) => {
  const persistDashboardScrollState =
    useCallback((sectionKey?: string) => {
      const currentScrollTop =
        getCurrentScrollTop();

      let activeSection: HTMLElement | null =
        null;

      if (sectionKey) {
        activeSection =
          document.querySelector<HTMLElement>(
            `[data-dashboard-section="${sectionKey}"]`,
          );
      }

      if (!activeSection) {
        activeSection =
          findCurrentDashboardSection(
            currentScrollTop,
          );
      }

      window.sessionStorage.setItem(
        DASHBOARD_SCROLL_POSITION_KEY,
        String(currentScrollTop),
      );

      const activeSectionKey =
        activeSection?.dataset.dashboardSection;

      if (!activeSectionKey) {
        return;
      }

      const sectionTop =
        activeSection.getBoundingClientRect().top +
        window.scrollY;

      window.sessionStorage.setItem(
        DASHBOARD_SCROLL_SECTION_KEY,
        activeSectionKey,
      );

      window.sessionStorage.setItem(
        DASHBOARD_SCROLL_SECTION_OFFSET_KEY,
        String(
          Math.max(
            0,
            currentScrollTop - sectionTop,
          ),
        ),
      );
    }, []);

  const clearSavedScrollState =
    useCallback(() => {
      window.sessionStorage.removeItem(
        DASHBOARD_SCROLL_POSITION_KEY,
      );

      window.sessionStorage.removeItem(
        DASHBOARD_SCROLL_SECTION_KEY,
      );

      window.sessionStorage.removeItem(
        DASHBOARD_SCROLL_SECTION_OFFSET_KEY,
      );
    }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    const savedScrollPosition =
      window.sessionStorage.getItem(
        DASHBOARD_SCROLL_POSITION_KEY,
      );

    const savedSectionKey =
      window.sessionStorage.getItem(
        DASHBOARD_SCROLL_SECTION_KEY,
      );

    const savedSectionOffset =
      window.sessionStorage.getItem(
        DASHBOARD_SCROLL_SECTION_OFFSET_KEY,
      );

    if (
      !savedScrollPosition &&
      !savedSectionKey
    ) {
      return;
    }

    if (
      savedSectionKey &&
      savedSectionKey !== selectedCategory
    ) {
      setSelectedCategory(savedSectionKey);
      return;
    }

    const scrollTop = Number(
      savedScrollPosition ?? 0,
    );

    const sectionOffset = Number(
      savedSectionOffset ?? 0,
    );

    if (Number.isNaN(scrollTop)) {
      clearSavedScrollState();
      return;
    }

    let attemptCount = 0;
    let timerId: number | undefined;

    const restoreScroll = () => {
      attemptCount += 1;

      let targetScrollTop = scrollTop;

      if (savedSectionKey) {
        const sectionElement =
          document.querySelector<HTMLElement>(
            `[data-dashboard-section="${savedSectionKey}"]`,
          );

        if (sectionElement) {
          const sectionTop =
            sectionElement.getBoundingClientRect()
              .top + window.scrollY;

          targetScrollTop = Math.max(
            0,
            Math.round(
              sectionTop +
                (Number.isNaN(sectionOffset)
                  ? 0
                  : sectionOffset),
            ),
          );
        }
      }

      window.scrollTo({
        top: targetScrollTop,
        behavior: "auto",
      });

      const currentScrollTop =
        getCurrentScrollTop();

      const maxScrollTop = Math.max(
        0,
        document.documentElement.scrollHeight -
          window.innerHeight,
      );

      const expectedScrollTop = Math.min(
        targetScrollTop,
        maxScrollTop,
      );

      const reachedTarget =
        Math.abs(
          currentScrollTop -
            expectedScrollTop,
        ) <= 4;

      if (
        reachedTarget ||
        attemptCount >= 12
      ) {
        clearSavedScrollState();
        return;
      }

      timerId = window.setTimeout(
        restoreScroll,
        120,
      );
    };

    timerId = window.setTimeout(
      restoreScroll,
      0,
    );

    return () => {
      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    };
  }, [
    loading,
    dependency,
    selectedCategory,
    setSelectedCategory,
    clearSavedScrollState,
  ]);

  return {
    persistDashboardScrollState,
  };
};

export default useDashboardScroll;